import { useRef, useState } from 'react'
import { ApiError } from '../api/client'
import { useDeleteAvatar, useUploadAvatar } from '../api/hooks'

/** Palette déterministe d'après le prénom (fallback initiales) */
const PALETTE = [
  ['#0ea5e9', '#0369a1'], // ocean
  ['#f04a23', '#b32c14'], // coral
  ['#d4a574', '#8b6f47'], // sand
  ['#f97316', '#c2410c'], // sunset
  ['#10b981', '#047857'], // green
  ['#8b5cf6', '#6d28d9'], // violet
  ['#ec4899', '#be185d'], // pink
  ['#14b8a6', '#0f766e'], // teal
]
const hash = (s: string) => {
  let h = 0
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0
  return Math.abs(h)
}
const colorOf = (prenom: string) => PALETTE[hash(prenom) % PALETTE.length]
const initialOf = (prenom: string) => prenom.trim()[0]?.toUpperCase() ?? '?'

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
const SIZE_PX: Record<Size, number> = { xs: 22, sm: 30, md: 40, lg: 60, xl: 96 }
const TEXT_CLS: Record<Size, string> = {
  xs: 'text-[10px]', sm: 'text-xs', md: 'text-sm',
  lg: 'text-xl', xl: 'text-3xl',
}

export interface AvatarProps {
  personne_id: number
  prenom: string
  avatar_version: number
  size?: Size
  className?: string
  /** Bouton d'upload directement intégré (Familles) */
  editable?: boolean
  onUploaded?: () => void
}

export default function Avatar({
  personne_id, prenom, avatar_version, size = 'sm', className = '',
  editable = false, onUploaded,
}: AvatarProps) {
  const [c1, c2] = colorOf(prenom)
  const px = SIZE_PX[size]
  const has = avatar_version > 0
  const src = has ? `/avatars/${personne_id}.jpg?v=${avatar_version}` : null

  const upload = useUploadAvatar()
  const del = useDeleteAvatar()
  const fileRef = useRef<HTMLInputElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    setErr(null)
    upload.mutate(
      { id: personne_id, file: f },
      {
        onSuccess: () => { setMenuOpen(false); onUploaded?.() },
        onError: (e) => setErr(e instanceof ApiError ? e.detail : 'Erreur upload'),
      },
    )
    e.target.value = '' // reset pour pouvoir reupload le même fichier
  }

  const handleDelete = () => {
    if (!has) return
    if (!confirm(`Supprimer la photo de ${prenom} ?`)) return
    del.mutate(personne_id, { onSuccess: () => setMenuOpen(false) })
  }

  const inner = (
    <div
      className={`relative rounded-full overflow-hidden shrink-0 ring-1 ring-white shadow-sm ${className}`}
      style={{ width: px, height: px }}
      aria-label={`Avatar de ${prenom}`}
      title={prenom}
    >
      {src ? (
        <img
          src={src}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          draggable={false}
        />
      ) : (
        <div
          className={`w-full h-full flex items-center justify-center font-bold text-white ${TEXT_CLS[size]}`}
          style={{ background: `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)` }}
          aria-hidden
        >
          {initialOf(prenom)}
        </div>
      )}
      {upload.isPending && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
          <div className="w-1/2 h-1/2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        </div>
      )}
    </div>
  )

  if (!editable) return inner

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setMenuOpen(v => !v)}
        className="group relative block"
        aria-label={`Modifier l'avatar de ${prenom}`}
      >
        {inner}
        <span
          className="absolute -bottom-0.5 -right-0.5 bg-ocean-500 text-white rounded-full w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center text-[9px] sm:text-[10px] ring-1 ring-white"
          aria-hidden
        >📷</span>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handlePick}
        className="hidden"
      />
      {menuOpen && (
        <>
          {/* clickaway */}
          <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
          <div className="absolute top-full left-0 mt-1 bg-white border border-ocean-100 rounded-lg shadow-card py-1 z-50 min-w-[140px] anim-in">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-ocean-50"
            >
              📷 {has ? 'Remplacer la photo' : 'Ajouter une photo'}
            </button>
            {has && (
              <button
                type="button"
                onClick={handleDelete}
                className="w-full text-left px-3 py-1.5 text-sm text-coral-700 hover:bg-coral-50"
              >
                🗑️ Retirer la photo
              </button>
            )}
            {err && <div className="px-3 py-1.5 text-xs text-coral-700">{err}</div>}
          </div>
        </>
      )}
    </div>
  )
}
